import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { AuthSplitLayout } from '../../components/auth/AuthSplitLayout';
import { showErrorToast, showSuccessToast } from '../../utils/errorHandler';
import { ROUTES } from '../../utils/constants';
import { validateEmail, validatePassword } from '../../utils/validation';

interface RegisterFormData {
  email: string;
  password: string;
  confirmPassword: string;
  role: 'PARENT' | 'STUDENT';
}

const initialTouched = { role: false, email: false, password: false, confirm: false };

export const RegisterPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(initialTouched);
  const { register: registerUser } = useAuth();
  const navigate = useNavigate();

  const {
    register: registerField,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      role: 'PARENT' as 'PARENT' | 'STUDENT',
    },
  });

  const password = watch('password');

  const showRoleError = (submitted || touched.role) && errors.role?.message;
  const showEmailError = (submitted || touched.email) && errors.email?.message;
  const showPasswordError = (submitted || touched.password) && errors.password?.message;
  const showConfirmError = (submitted || touched.confirm) && errors.confirmPassword?.message;

  const onSubmit = async (data: RegisterFormData) => {
    setSubmitted(true);
    if (data.password !== data.confirmPassword) {
      showErrorToast(new Error('Passwords do not match'));
      return;
    }

    setIsLoading(true);
    try {
      await registerUser(data.email, data.password, data.role);
      showSuccessToast('Registration successful!');
      navigate(ROUTES.LOGIN);
    } catch (error) {
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  const roleRegister = registerField('role', { required: 'Role is required' });
  const emailRegister = registerField('email', {
    required: 'Email is required',
    validate: (value) => validateEmail(value) || 'Invalid email address',
  });
  const passwordRegister = registerField('password', {
    required: 'Password is required',
    validate: (value) => {
      const result = validatePassword(value);
      return result.valid || result.message;
    },
  });
  const confirmRegister = registerField('confirmPassword', {
    required: 'Please confirm your password',
    validate: (value) => value === password || 'Passwords do not match',
  });

  return (
    <AuthSplitLayout showBackButton={false}>
      <div className="space-y-8">
        <button
          type="button"
          onClick={() => navigate(ROUTES.LANDING)}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors -ml-1"
          aria-label="Back to home"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Sign up</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Create an account to get started
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <Select
            label="I am a"
            options={[
              { value: 'PARENT', label: 'Parent' },
              { value: 'STUDENT', label: 'Student' },
            ]}
            {...roleRegister}
            onBlur={(e) => {
              roleRegister.onBlur(e);
              setTouched((prev) => ({ ...prev, role: true }));
            }}
            error={showRoleError ? errors.role?.message : undefined}
          />

          <Input
            label="Email"
            type="email"
            {...emailRegister}
            onBlur={(e) => {
              emailRegister.onBlur(e);
              setTouched((prev) => ({ ...prev, email: true }));
            }}
            error={showEmailError ? errors.email?.message : undefined}
            placeholder="you@example.com"
          />

          <Input
            label="Password"
            type="password"
            {...passwordRegister}
            onBlur={(e) => {
              passwordRegister.onBlur(e);
              setTouched((prev) => ({ ...prev, password: true }));
            }}
            error={showPasswordError ? errors.password?.message : undefined}
            placeholder="Create a strong password"
            helperText="Must be at least 8 characters with uppercase, lowercase, and number"
          />

          <Input
            label="Confirm Password"
            type="password"
            {...confirmRegister}
            onBlur={(e) => {
              confirmRegister.onBlur(e);
              setTouched((prev) => ({ ...prev, confirm: true }));
            }}
            error={showConfirmError ? errors.confirmPassword?.message : undefined}
            placeholder="Confirm your password"
          />

          <Button type="submit" className="w-full" isLoading={isLoading}>
            Create Account
          </Button>

          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link
              to={ROUTES.LOGIN}
              className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              Sign in
            </Link>
          </p>
        </form>
      </div>
    </AuthSplitLayout>
  );
};
